// Centralized category metadata for Unified dashboards and UI
export interface CategoryTabDef {
  id: string;
  label: string;
  iconName?: string;
  description?: string;
}

// Unified category configuration: color, title/description, main tabs, and dropdown groups
export interface CategoryConfig {
  title: string;
  description: string;
  color: string;
  route: string; // relative to '/app/'
  iconName?: string;
  cardDescription?: string; // short copy for overall page cards only
  tabs: CategoryTabDef[];
  dropdowns?: Record<string, CategoryTabDef[]>;
}

export const CATEGORIES: Record<string, CategoryConfig> = {
  "overview": {
    title: "Overall",
    description: "Comprehensive analysis across all onchain sectors.",
    color: "blue",
    route: "sectors/overall",
    iconName: "grid-2x2",
    tabs: [],
  },
  "bitcoin": {
    title: "Bitcoin",
    description: "Bitcoin ecosystem and related protocols.",
    color: "orange",
    route: "tokens/btc",
    iconName: "bitcoin",
    tabs: [],
  },
  "cex-tokens": {
    title: "CEX Tokens",
    description: "Centralized exchange tokens and their performance.",
    color: "blue",
    route: "sectors/cex-tokens",
    iconName: "banknote",
    tabs: [],
  },
  "core-infrastructure": {
    title: "Core Blockchain Layers",
    description: "Layer 1 and Layer 2 blockchain networks, data availability layers, and foundational blockchain infrastructure.",
    color: "purple",
    route: "sectors/core-infrastructure",
    iconName: "cpu",
    cardDescription: "Layer 1, Layer 2, and data availability layers.",
    tabs: [
      { id: "layer1", label: "Layer 1", iconName: "cpu", description: "Layer 1 blockchain networks and protocols" },
      { id: "layer2", label: "Layer 2", iconName: "layers", description: "Layer 2 scaling solutions and networks" },
      { id: "dataAvailabilityLayers", label: "Data Availability", iconName: "database", description: "Data availability layer protocols" },
    ],
  },
  "defi": {
    title: "DeFi",
    description: "Decentralized finance protocols including DEXs, lending platforms, yield farming, and DeFi infrastructure.",
    color: "green",
    route: "sectors/defi",
    iconName: "coins",
    cardDescription: "Decentralized finance protocols and platforms.",
    tabs: [
      { id: "dex", label: "DEXs", iconName: "refresh-cw", description: "Decentralized exchange protocols" },
      { id: "lendingBorrowing", label: "Lending & Borrowing", iconName: "credit-card", description: "Lending and borrowing protocols" },
      { id: "yieldFarming", label: "Yield Farming", iconName: "trending-up", description: "Yield farming and liquidity mining protocols" },
      { id: "aggregators", label: "Aggregators", iconName: "shuffle", description: "DEX aggregator platforms" },
      { id: "perpetuals", label: "Perpetuals", iconName: "bar-chart-3", description: "Perpetual and derivatives platforms" },
      { id: "staking", label: "Liquid Staking", iconName: "lock", description: "Liquid staking and staking infrastructure" },
      { id: "otherDefi", label: "Other DeFi", iconName: "more-horizontal", description: "Other DeFi platforms and protocols" },
    ],
    dropdowns: {
      staking: [
        { id: "protocolsLst", label: "Protocols", iconName: "layers", description: "Liquid staking protocols and platforms" },
        { id: "stakedAssets", label: "Staked Assets", iconName: "coins", description: "Staked asset tokens and derivatives" },
      ],
    },
  },
  "emerging": {
    title: "Emerging Sectors",
    description: "Innovative blockchain sectors including AI protocols, gaming, social platforms, privacy solutions, and DePIN networks.",
    color: "pink",
    route: "sectors/emerging",
    iconName: "flask-conical",
    cardDescription: "AI, Gaming, SocialFi, Privacy, and DePIN protocols.",
    tabs: [
      { id: "ai", label: "AI", iconName: "cpu", description: "AI and machine learning protocols" },
      { id: "gamingGameFi", label: "Gaming & GameFi", iconName: "gamepad-2", description: "Gaming and GameFi platforms" },
      { id: "socialFi", label: "SocialFi", iconName: "users", description: "Social finance platforms and protocols" },
      { id: "privacy", label: "Privacy", iconName: "shield", description: "Privacy-focused platforms and solutions" },
      { id: "depin", label: "DePIN", iconName: "wifi", description: "Decentralized Physical Infrastructure Networks" },
    ],
    dropdowns: {
      depin: [
        { id: "mapping", label: "Mapping", iconName: "map-pin", description: "Mapping protocols and platforms" },
        { id: "telecom", label: "Telecom", iconName: "phone-call", description: "Telecom infrastructure protocols" },
        { id: "storageFileSharing", label: "Storage & File Sharing", iconName: "hard-drive", description: "Storage and file sharing protocols" },
        { id: "gpus", label: "GPUs", iconName: "cpu", description: "GPU trading and investment platforms" },
      ],
    },
  },
  "assets": {
    title: "Real World Assets",
    description: "Traditional assets tokenized on blockchain including commodities, bonds, and institutional funds.",
    color: "indigo",
    route: "sectors/assets",
    iconName: "building-2",
    cardDescription: "RWA protocols and traditional asset tokenization.",
    tabs: [
      { id: "protocolsRwa", label: "RWA Protocols", iconName: "building-2", description: "Real world asset protocols" },
      { id: "commodities", label: "Commodities", iconName: "gem", description: "Commodity trading and investment platforms" },
      { id: "globalBonds", label: "Global Bonds", iconName: "file-text", description: "Global bond markets and instruments" },
      { id: "institutionalFunds", label: "Institutional Funds", iconName: "briefcase", description: "Institutional investment funds and products" },
      { id: "privateCredit", label: "Private Credit", iconName: "credit-card", description: "Private credit and lending platforms" },
      { id: "stocks", label: "Stocks", iconName: "trending-up", description: "Stock trading and investment platforms" },
    ],
  },
  "infrastructure": {
    title: "Infrastructure & Middleware",
    description: "Blockchain infrastructure services including oracles, bridges, indexing protocols, and middleware solutions.",
    color: "cyan",
    route: "sectors/infrastructure",
    iconName: "server",
    cardDescription: "Oracles, bridges, indexing, and middleware protocols.",
    tabs: [
      { id: "oracles", label: "Oracles", iconName: "eye", description: "Blockchain oracle networks and protocols" },
      { id: "interoperabilityBridges", label: "Bridges", iconName: "link", description: "Cross-chain bridge and interoperability protocols" },
      { id: "indexingData", label: "Indexing", iconName: "search", description: "Data indexing and query protocols" },
      { id: "wrappedAssets", label: "Wrapped Assets", iconName: "package", description: "Wrapped asset protocols and platforms" },
      { id: "identity", label: "Identity", iconName: "fingerprint", description: "Decentralized identity and DID middleware" },
    ],
  },
  "stablecoins": {
    title: "Stablecoins",
    description: "Stablecoin protocols, assets, and yield-bearing stablecoin products across different blockchain networks.",
    color: "emerald",
    route: "sectors/stablecoins",
    iconName: "circle-dollar-sign",
    cardDescription: "Stablecoin protocols and assets.",
    tabs: [
      { id: "stablecoin-protocols", label: "Stablecoin Protocols", iconName: "settings", description: "Stablecoin protocol platforms" },
      { id: "stablecoin-assets", label: "Stablecoin Assets", iconName: "coins", description: "Stablecoin assets and tokens" },
      { id: "stablecoin-yield", label: "Yield Bearing Stablecoins", iconName: "trending-up", description: "Yield-bearing stablecoin protocols" },
    ],
  },
  "bitcoin-ecosystem": {
    title: "Bitcoin Ecosystem",
    description: "Bitcoin-related protocols including sidechains, Layer 2 solutions, and Bitcoin forks.",
    color: "amber",
    route: "sectors/bitcoin-ecosystem",
    iconName: "layers",
    cardDescription: "Bitcoin sidechains, L2s, and forks.",
    tabs: [
      { id: "sideChainsBitcoinL2s", label: "Sidechains & Bitcoin L2s", iconName: "layers", description: "Bitcoin sidechains and Layer 2 solutions" },
      { id: "forks", label: "Forks", iconName: "git-branch", description: "Bitcoin forks and related projects" },
    ],
  },
  "miscellaneous": {
    title: "Miscellaneous",
    description: "Miscellaneous cryptocurrency categories including memecoins and other token types.",
    color: "slate",
    route: "sectors/miscellaneous",
    iconName: "package-2",
    cardDescription: "Memecoins and other miscellaneous protocols.",
    tabs: [
      { id: "memecoins", label: "Memecoins", iconName: "smile", description: "Meme-based cryptocurrency tokens" },
      { id: "memecoin-platforms", label: "Memecoin Platforms", iconName: "rocket", description: "Platforms for creating and trading memecoins" },
      { id: "other", label: "Other", iconName: "more-horizontal", description: "Other miscellaneous cryptocurrency categories" },
    ],
  },
};

// Backwards-compatible derived exports
export const CATEGORY_COLORS: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORIES)
    .filter(([k]) => k !== 'overview' && k !== 'bitcoin')
    .map(([k, v]) => [k, v.color])
);

export const CATEGORY_METADATA: Record<string, { title: string; description: string }> = Object.fromEntries(
  Object.entries(CATEGORIES)
    .filter(([k]) => k !== 'overview' && k !== 'bitcoin')
    .map(([k, v]) => [k, { title: v.title, description: v.description }])
);

export const CATEGORY_TABS: Record<string, CategoryTabDef[]> = (() => {
  const tabs: Record<string, CategoryTabDef[]> = {};
  Object.entries(CATEGORIES).forEach(([k, v]) => {
    tabs[k] = v.tabs || [];
    if (v.dropdowns) {
      Object.entries(v.dropdowns).forEach(([groupKey, groupTabs]) => {
        tabs[groupKey] = groupTabs;
      });
    }
  });
  return tabs;
})();

// Helper to construct unified dashboard paths
export const getUnifiedPath = (
  tabId: string,
  subId?: string,
  subSubId?: string
): string => {
  const base = `/app/sectors/${tabId}`;
  if (!subId) return base;
  const mid = `${base}/${subId}`;
  if (!subSubId) return mid;
  return `${mid}/${subSubId}`;
};
// end of file.
